import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Divider, Grid, Box } from '@mui/material';
import { IDENTITY_DOCS as generalInfoDoc, IdentityDocumentType } from '~/constants/docs.ts';
import { FormDataRevoke, ApplicationRevokeFormValues } from '../types.ts';
import { useDocumentTypesRevoke } from '../hooks/useDocumentTypesRevoke.ts';
import { SectionTitle } from '../../components/styled';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { FormDateInput } from '~/components/FormComponents/FormDateInput.tsx';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete.tsx';

interface ApplicationRevokeFormProps {
  formData?: FormDataRevoke;
}

export function ApplicationRevokeForm({ formData }: ApplicationRevokeFormProps) {
  const { watch, setValue } = useFormContext<ApplicationRevokeFormValues>();
  const { docTypes } = useDocumentTypesRevoke();
  const documentType = watch('documentType');

  const handleNumericChange =
    (fieldName: keyof ApplicationRevokeFormValues, maxLength: number) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const numericValue = event.target.value.replace(/\D/g, '').slice(0, maxLength);
      setValue(fieldName, numericValue, {
        shouldTouch: true,
        shouldValidate: true,
        shouldDirty: true,
      });
    };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormInput
            fullWidth
            name="lastName"
            disabled
            label="Прізвище"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormInput
            fullWidth
            name="firstName"
            disabled
            label="Ім'я"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormInput
            fullWidth
            name="middleName"
            disabled
            label="По батькові (за наявності)"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormInput
            fullWidth
            name="identificationNumber"
            disabled
            label="РНОКПП"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormDateInput
            name="birthDate"
            disabled
            label="Дата народження"
            isCurrentMaxDate
            placeholder="Введіть дату народження"
            dataTestId="birthDate"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormAutocomplete
            name="documentType"
            fullWidth
            disabled
            label="Тип документа"
            options={docTypes}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormInput
            fullWidth
            name="registryRecord"
            disabled
            label="Запис у реєстрі"
            inputProps={{
              maxLength: 200,
              minLength: 1,
            }}
          />
        </Grid>

        {documentType?.value !==
          String(generalInfoDoc[IdentityDocumentType.ukrainianCitizenPassportPlastic]) && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormInput
              fullWidth
              name="documentSeries"
              disabled
              label="Серія документа"
              inputProps={{
                maxLength: 200,
                minLength: 1,
              }}
            />
          </Grid>
        )}

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormInput
            fullWidth
            name="documentNumber"
            disabled
            label="Номер документа"
            inputProps={{
              maxLength: 200,
              minLength: 1,
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormDateInput
            name="issueDate"
            disabled
            label="Дата видачі документа"
            placeholder="Введіть дату видачі"
            dataTestId="issueDate"
          />
        </Grid>

        {documentType?.value ===
          String(generalInfoDoc[IdentityDocumentType.ukrainianCitizenPassport]) && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormDateInput
              name="documentExpiryDate"
              disabled
              label="Дата закінчення документа"
              placeholder="Введіть дату закінчення документу"
              dataTestId="documentExpiryDate"
            />
          </Grid>
        )}

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormInput
            fullWidth
            name="issuingAuthority"
            disabled
            label="Назва органу, що видав документ"
            inputProps={{
              maxLength: 200,
              minLength: 1,
            }}
          />
        </Grid>

        {formData?.financialServices?.selected &&
          formData?.financialServices?.clientType === 'REPRESENTATIVE' && (
            <>
              <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <SectionTitle data-testid="financialServicesTitle">
                  Надання Банком фінансових послуг
                </SectionTitle>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormInput
                  fullWidth
                  name="financialServicesCounterpartyName"
                  label="Найменування / прізвище, ім’я , по батькові (за наявності) клієнта"
                  placeholder="Введіть назву / ПІБ клієнта"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormInput
                  fullWidth
                  name="financialServicesCounterpartyCode"
                  label="ЄДРПОУ/РНОКПП"
                  placeholder="Введіть ЄДРПОУ/РНОКПП"
                  onChange={handleNumericChange('financialServicesCounterpartyCode', 10)}
                  inputProps={{
                    maxLength: 10,
                  }}
                />
              </Grid>
            </>
          )}

        {formData?.businessRelationship?.selected &&
          formData?.businessRelationship?.clientType === 'REPRESENTATIVE' && (
            <>
              <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <SectionTitle data-testid="businessRelationshipTitle">
                  Встановлення та підтримання ділових (договірних) відносин
                </SectionTitle>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormInput
                  fullWidth
                  name="businessRelationCounterpartyName"
                  label="Найменування (назва) контрагента"
                  placeholder="Введіть назву / ПІБ контрагента"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormInput
                  fullWidth
                  name="businessRelationCounterpartyCode"
                  label="Код ЄДРПОУ"
                  placeholder="Введіть код ЄДРПОУ"
                  onChange={handleNumericChange('businessRelationCounterpartyCode', 10)}
                  inputProps={{
                    maxLength: 10,
                  }}
                />
              </Grid>
            </>
          )}
      </Grid>
    </Box>
  );
}
